/*
 * Copyright (c) CIRROM 2026.
 */

import { useSyncExternalStore } from "react";
import {
  EmailVerification,
  isEmailVerificationHttpError,
  verifyEmailAddress,
} from "../../../api/emailverifications";

/*
 * Verdicts, held once per address for as long as the app is open.
 *
 * A verdict belongs to an ADDRESS, not to the row that asked for it: the
 * same guessed work email can sit on the account follow-on row, on a pending
 * ledger candidate, and on the next record over, and Allegrow's answer is the
 * same for all of them. So the store is module-level and keyed by the
 * normalised address, every control reads through it, and the card's bulk
 * approve can consult it without threading state through four components.
 *
 * Nothing here calls the endpoint on its own. A check costs a credit, so it
 * runs on a steward's action, never on load - and picking an account counts
 * as one: the account follow-ons check their suggested email on selection.
 */

export type VerificationTone = "good" | "caution" | "bad" | "unknown";

export interface EmailVerdict {
  tone: VerificationTone;
  /** The ramp band that colours the marker; null when the verdict is neutral. */
  rampBand: string | null;
  /** The verdict in the steward's words: "Deliverable", "Mailbox doesn't exist". */
  headline: string;
  /** Why, one hover away. */
  why: string;
  /**
   * The clause that names what Allegrow reported, for the write control's
   * tooltip on a verdict that blocks the write. Absent when nothing blocks.
   */
  blockPhrase?: string;
}

export type VerificationEntry =
  | { phase: "checking" }
  | { phase: "verified"; result: EmailVerification; verdict: EmailVerdict }
  | { phase: "failed"; message: string; retryable: boolean };

export const normalizeEmail = (email: string): string =>
  email.trim().toLowerCase();

/** Fields whose value is an address a steward might write. */
export const isEmailField = (fieldName: string): boolean =>
  fieldName === "primary_email" ||
  fieldName === "secondary_email" ||
  fieldName === "email";

/*
 * Deliverability is a graded "how good is this record", so it wears the one
 * ramp - the same bands the dossier's own deliverability facts use - with
 * the words carrying the meaning and the colour only confirming it.
 */
export const describeVerification = (
  result: EmailVerification
): EmailVerdict => {
  switch (result.status) {
    case "SAFE":
      return {
        tone: "good",
        rampBand: "Outstanding",
        headline: "Deliverable",
        why: "Allegrow confirmed this mailbox accepts email.",
      };
    case "SOME_RISK":
      return {
        tone: "caution",
        rampBand: "Average",
        headline: result.is_catch_all
          ? "Deliverable, but the domain accepts any address"
          : "Deliverable, but the mailbox couldn't be confirmed",
        why: result.is_catch_all
          ? "A catch-all domain accepts mail for every address, so Allegrow cannot tell whether this particular mailbox exists."
          : "Allegrow could not confirm the mailbox exists, though the domain accepts mail.",
      };
    case "BLOCK_BOUNCE_RISK":
      return {
        tone: "bad",
        rampBand: "Critical",
        headline: "Likely to bounce",
        why: "Allegrow rates this address a high bounce risk.",
        blockPhrase: "this address is likely to bounce",
      };
    case "DEAD_EMAIL":
      return {
        tone: "bad",
        rampBand: "Critical",
        headline: "Mailbox doesn't exist",
        why: "Allegrow found no mailbox at this address.",
        blockPhrase: "this mailbox doesn't exist",
      };
    case "DO_NOT_MAIL_ABUSE":
      return {
        tone: "bad",
        rampBand: "Critical",
        headline: "Do not mail: abuse reported",
        why: "This address is known for abuse or spam complaints.",
        blockPhrase: "this address has reported abuse",
      };
    default:
      return {
        tone: "unknown",
        rampBand: null,
        headline: "Couldn't be classified",
        why: result.provider_status
          ? `Allegrow answered "${result.provider_status}", which Cirrom does not recognise.`
          : "Allegrow answered with a status Cirrom does not recognise.",
      };
  }
};

/** "checked just now", "checked 3 days ago" - from `last_verified_at`. */
export const describeCheckedAgo = (
  isoTimestamp: string | null,
  now: number = Date.now()
): string => {
  if (!isoTimestamp) return "checked";
  // Python emits microseconds; not every Date parser accepts six digits.
  const parsed = Date.parse(isoTimestamp.replace(/(\.\d{3})\d+/, "$1"));
  if (Number.isNaN(parsed)) return "checked";
  const minutes = Math.max(0, Math.round((now - parsed) / 60000));
  if (minutes < 1) return "checked just now";
  if (minutes < 60)
    return `checked ${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `checked ${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `checked ${days} day${days === 1 ? "" : "s"} ago`;
};

/* ---- The store ---------------------------------------------------------- */

const entries = new Map<string, VerificationEntry>();
/** Set once a 402 or 503 comes back; the whole session stops offering checks. */
let unavailableReason: string | null = null;
let version = 0;
const listeners = new Set<() => void>();

const emit = () => {
  version += 1;
  listeners.forEach((listener) => listener());
};

const setEntry = (key: string, entry: VerificationEntry) => {
  entries.set(key, entry);
  emit();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getVerification = (
  email: string | null | undefined
): VerificationEntry | undefined =>
  email ? entries.get(normalizeEmail(email)) : undefined;

export const getVerificationUnavailableReason = (): string | null =>
  unavailableReason;

/** Test seam: the store outlives a render, so specs start clean. */
export const resetEmailVerifications = () => {
  entries.clear();
  unavailableReason = null;
  emit();
};

/**
 * Whether Allegrow's verdict on this address forbids writing it. Unverified
 * is not red: nothing changes until the steward has asked.
 */
export const isEmailWriteBlocked = (
  email: string | null | undefined
): boolean => {
  const entry = getVerification(email);
  return entry?.phase === "verified" && Boolean(entry.verdict.blockPhrase);
};

/**
 * The write control's tooltip for an address, given what it would say with
 * no verdict. A blocking verdict replaces the sentence with why the write is
 * refused and what to do instead - the caller names the way out, because a
 * ledger row has a Reject and the account follow-on row does not; a caution
 * prefixes it.
 */
export const describeWriteAction = (
  email: string | null | undefined,
  base: string,
  verb: "written" | "approved",
  alternative: string
): string => {
  const entry = getVerification(email);
  if (entry?.phase !== "verified") return base;
  const { verdict } = entry;
  if (verdict.blockPhrase) {
    return `Allegrow reports ${verdict.blockPhrase}, so it can't be ${verb}. ${alternative}`;
  }
  if (verdict.tone === "caution") {
    return `${verdict.headline}. ${base}`;
  }
  return base;
};

/** PENDING means another check on this address is mid-flight elsewhere. */
const PENDING_RETRY_MS = 3000;
const PENDING_RETRY_LIMIT = 20;
const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

export const requestEmailVerification = async (
  email: string,
  options: {
    force?: boolean;
    crmContactId?: string | null;
    integratedSystemId?: number | null;
  } = {}
): Promise<void> => {
  const key = normalizeEmail(email);
  if (!key || entries.get(key)?.phase === "checking" || unavailableReason) {
    return;
  }
  setEntry(key, { phase: "checking" });
  try {
    let result = await verifyEmailAddress({ email: key, ...options });
    let attempts = 0;
    while (result.status === "PENDING" && attempts < PENDING_RETRY_LIMIT) {
      await wait(PENDING_RETRY_MS);
      // The same request, never forced: the first check's verdict is what
      // comes back once it lands.
      result = await verifyEmailAddress({
        ...options,
        email: key,
        force: false,
      });
      attempts += 1;
    }
    if (result.status === "PENDING") {
      setEntry(key, {
        phase: "failed",
        message:
          "Allegrow is still checking this address. Try again in a moment.",
        retryable: true,
      });
      return;
    }
    if (result.status === "ERROR") {
      setEntry(key, {
        phase: "failed",
        message: result.last_error
          ? `Allegrow couldn't verify this address: ${result.last_error}`
          : "Allegrow couldn't verify this address.",
        retryable: true,
      });
      return;
    }
    setEntry(key, {
      phase: "verified",
      result,
      verdict: describeVerification(result),
    });
  } catch (error) {
    const status = isEmailVerificationHttpError(error) ? error.status : 0;
    if (status === 402 || status === 503) {
      /*
       * Commercial or configuration states, not something a retry fixes.
       * The address goes back to unverified and every trigger on the surface
       * says why it is disabled.
       */
      unavailableReason =
        status === 402
          ? "Allegrow's verification quota is used up, so addresses can't be checked right now."
          : "Email verification isn't configured on this install.";
      entries.delete(key);
      emit();
      return;
    }
    setEntry(key, {
      phase: "failed",
      message:
        status === 400
          ? "That isn't a valid email address."
          : status === 502
          ? "Allegrow couldn't be reached. Try again."
          : "The check didn't complete. Try again.",
      retryable: status !== 400,
    });
  }
};

/** One address's entry, live. */
export const useEmailVerification = (email: string | null | undefined) => {
  const entry = useSyncExternalStore(subscribe, () => getVerification(email));
  const unavailable = useSyncExternalStore(subscribe, () => unavailableReason);
  return { entry, unavailable };
};

/**
 * A counter that moves whenever any verdict changes, for a component that
 * reads the store through the plain getters (the card's bulk approve list)
 * and needs to recompute when they would answer differently.
 */
export const useEmailVerificationVersion = (): number =>
  useSyncExternalStore(subscribe, () => version);
